'use client'

import { motion } from "framer-motion";
import { Hotel, TrendingUp, Crown, ChevronRight } from "lucide-react";

const years = [
  {
    num: 1,
    title: "Year 1 – Hospitality Foundations & Operations",
    icon: Hotel,
    semesters: [
      "Semester 1 – Core Hospitality Foundations",
      "Semester 2 – Operational Excellence",
    ],
    featured: false,
  },
  {
    num: 2,
    title: "Year 2 – Advanced Hospitality Skills",
    icon: TrendingUp,
    semesters: [
      "Semester 3 – Advanced Operational and Management Skills",
      "Semester 4 – Business & Leadership Skills",
    ],
    featured: false,
  },
  {
    num: 3,
    title: "Year 3 – Hospitality Leadership & Management",
    icon: Crown,
    semesters: [
      "Semester 5 – Hospitality Management Specialization",
      "Semester 6 – Industry Capstone Project",
    ],
    featured: true,
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.15, ease: "easeOut" },
  }),
};

const Curriculum = () => {
  return (
    <section
      id="curriculum"
      className="relative bg-max-w-(--max-page-width) mx-auto px-(--container-padding) w-full py-16 overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, hsl(220 20% 10%) 0%, hsl(220 25% 14%) 50%, hsl(220 20% 10%) 100%)",
      }}
    >

      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(hsl(0 0% 100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-[#F5A400] uppercase tracking-[4px] text-xs font-semibold mb-4">
            CURRICULUM
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-tight">
            Your 3-Year{" "}
            <span className="text-[#F5A400]">Structured Journey</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-0 items-stretch">
          {years.map((year, i) => {
            const Icon = year.icon;
            const isLast = i === years.length - 1;

            return (
              <div key={year.num} className="relative flex">
                <motion.div
                  custom={i}
                  variants={cardVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  whileHover={{ y: -8, scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className={`relative flex-1 rounded-2xl p-7 md:p-8 border transition-shadow duration-300 cursor-default ${
                    year.featured
                      ? "bg-linear-to-br from-[hsl(36_100%_55%/0.12)] to-[hsl(36_100%_55%/0.04)] border-[#F5A400] shadow-[0_0_40px_-10px_hsl(36_100%_55%/0.25)] hover:shadow-[0_0_60px_-10px_hsl(36_100%_55%/0.35)]"
                      : "bg-[hsl(220_20%_14%)] border-[hsl(220_15%_22%)] hover:border-[#F5A400] hover:shadow-[0_0_30px_-10px_hsl(36_100%_55%/0.15)]"
                  }`}
                >
                  <div
                    className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider mb-5 ${
                      year.featured
                        ? "bg-[#F5A400] text-[hsl(220_20%_10%)]"
                        : "bg-[hsl(220_15%_20%)] text-[hsl(220_10%_60%)]"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    Year {year.num}
                  </div>

                  {/* Title */}
                  <h3
                    className={`text-lg md:text-xl font-bold leading-snug mb-6 ${
                      year.featured ? "text-[#F5A400]" : "text-white"
                    }`}
                  >
                    {year.title}
                  </h3>

                  <div className="space-y-3">
                    {year.semesters.map((sem, si) => (
                      <div
                        key={si}
                        className="flex items-start gap-3 rounded-xl bg-[hsl(220_20%_10%/0.6)] border border-[hsl(220_15%_22%)] px-4 py-3"
                      >
                        <div
                          className={`shrink-0 w-2 h-2 rounded-full mt-1.5 ${
                            year.featured
                              ? "bg-[#F5A400]"
                              : "bg-[hsl(var(--primary))]"
                          }`}
                        />
                        <span className="text-sm text-[hsl(220_10%_70%)] font-medium leading-relaxed">
                          {sem}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Featured badge */}
                  {year.featured && (
                    <div className="mt-6 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#F5A400]">
                      <Crown className="w-3.5 h-3.5" />
                      Leadership Ready
                    </div>
                  )}
                </motion.div>

                {/* Arrow connector (desktop only) */}
                {!isLast && (
                  <div className="hidden md:flex items-center justify-center w-0 relative z-20">
                    <div className="absolute -right-3 w-6 h-6 rounded-full bg-[#F5A400] flex items-center justify-center shadow-[0_0_12px_hsl(36_100%_55%/0.4)]">
                      <ChevronRight className="w-3.5 h-3.5 text-[hsl(220_20%_10%)]" />
                    </div>
                  </div>
                )}

                {/* Arrow connector (mobile only) */}
                {!isLast && (
                  <div className="flex md:hidden justify-center -mb-3 -mt-3 relative z-20">
                    <div className="absolute -bottom-1.5 w-6 h-6 rounded-full bg-[#F5A400] flex items-center justify-center shadow-[0_0_12px_hsl(36_100%_55%/0.4)] rotate-90">
                      <ChevronRight className="w-3.5 h-3.5 text-[hsl(220_20%_10%)]" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Curriculum;
